import { useMemo, useEffect, ReactNode } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { ReactComponent as JiraIcon } from '@elementary/common-ui/assets/16x16/jira.svg';
import {
  Alert,
  Dialog,
  DropDown,
  FlexColumn,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  Loading,
  TextField,
  InternalLink,
} from '@elementary/common-ui/components';
import { ReactComponent as JiraIssueTypeBugIcon } from '@elementary/common-ui/assets/16x16/jira/issueType-bug.svg';
import { ReactComponent as JiraIssueTypeTaskIcon } from '@elementary/common-ui/assets/16x16/jira/issueType-task.svg';
import { ReactComponent as JiraIssueTypeStoryIcon } from '@elementary/common-ui/assets/16x16/jira/issueType-story.svg';
import { ReactComponent as JiraPriorityHighestIcon } from '@elementary/common-ui/assets/16x16/jira/priority-highest.svg';
import { ReactComponent as JiraPriorityHighIcon } from '@elementary/common-ui/assets/16x16/jira/priority-high.svg';
import { ReactComponent as JiraPriorityMediumIcon } from '@elementary/common-ui/assets/16x16/jira/priority-medium.svg';
import { ReactComponent as JiraPriorityLowIcon } from '@elementary/common-ui/assets/16x16/jira/priority-low.svg';
import { ReactComponent as JiraPriorityLowestIcon } from '@elementary/common-ui/assets/16x16/jira/priority-lowest.svg';
import type { CreateTicketDialogProps } from '../CreateTicketDialog';
import useJiraProjectFields from '../useJiraProjectFields';
import TicketCreationError from '../TicketCreationError';
import { JIRA_ISSUE_TYPE_VARIANTS } from './constants';
import { transformJiraFormValues } from './jiraFieldTransformers';
import JiraDynamicFields from './JiraDynamicFields';
import JiraUserAutocompleteField from './JiraUserAutocompleteField';

const ISSUE_TYPE_ICONS: Record<string, ReactNode> = {
  Bug: <JiraIssueTypeBugIcon />,
  Task: <JiraIssueTypeTaskIcon />,
  Story: <JiraIssueTypeStoryIcon />,
};

const PRIORITY_ICONS: Record<string, ReactNode> = {
  Highest: <JiraPriorityHighestIcon />,
  High: <JiraPriorityHighIcon />,
  Medium: <JiraPriorityMediumIcon />,
  Low: <JiraPriorityLowIcon />,
  Lowest: <JiraPriorityLowestIcon />,
};

const COMMON_FIELD_KEYS = [
  'summary',
  'description',
  'project',
  'issuetype',
  'reporter',
  'assignee',
  'priority',
];

const CreateJiraTicketDialog = ({
  open,
  onClose,
  onConfirm,
  integration,
  envId,
  isCreating,
  error,
  defaultFields,
  isLoadingDefaults = false,
  contextId,
}: CreateTicketDialogProps) => {
  const hasDefaultFields = isLoadingDefaults || defaultFields !== undefined;

  const [projectFieldsQuery] = useJiraProjectFields(envId, integration.id);
  const projectFieldsResponse = projectFieldsQuery?.data;
  const isFetchingFields = projectFieldsQuery?.isFetching ?? false;

  const projectFieldsData = projectFieldsResponse?.fields;
  const projectKey = projectFieldsResponse?.project_key;
  const baseUrl = projectFieldsResponse?.base_url;

  const issueTypeOptions = useMemo(() => {
    if (!projectFieldsData || Object.keys(projectFieldsData).length === 0) {
      return [];
    }

    const getCanonicalType = (name: string): string | undefined =>
      Object.entries(JIRA_ISSUE_TYPE_VARIANTS).find(([, variants]) =>
        variants.includes(name.toLowerCase()),
      )?.[0];

    return Object.keys(projectFieldsData).map((issueTypeName) => ({
      value: issueTypeName,
      label: issueTypeName,
      icon:
        ISSUE_TYPE_ICONS[issueTypeName] ||
        ISSUE_TYPE_ICONS[getCanonicalType(issueTypeName) ?? ''] ||
        undefined,
    }));
  }, [projectFieldsData]);

  const formMethods = useForm<Record<string, any>>({
    mode: 'onBlur',
  });

  const { watch, setValue, reset } = formMethods;

  useEffect(() => {
    if (issueTypeOptions.length > 0 && projectFieldsData) {
      const defaultIssueType =
        issueTypeOptions.find((option) =>
          JIRA_ISSUE_TYPE_VARIANTS.Task.includes(option.value.toLowerCase()),
        )?.value ||
        issueTypeOptions.find((option) =>
          JIRA_ISSUE_TYPE_VARIANTS.Story.includes(option.value.toLowerCase()),
        )?.value ||
        issueTypeOptions[0].value;

      if (!hasDefaultFields) {
        reset({ issueType: defaultIssueType });
        return;
      }

      if (!defaultFields) return;

      const reporterValue = defaultFields.reporter
        ? {
            value: defaultFields.reporter,
            label: defaultFields.reporter_display || defaultFields.reporter,
          }
        : null;
      const assigneeValue = defaultFields.assignee
        ? {
            value: defaultFields.assignee,
            label: defaultFields.assignee_display || defaultFields.assignee,
          }
        : null;

      const defaultIssueTypeFields = projectFieldsData[defaultIssueType] || [];
      const defaultPriorityField = defaultIssueTypeFields.find(
        (field) => field.key === 'priority',
      );
      const defaultPriority = defaultPriorityField?.default_value?.id || null;
      reset({
        issueType: defaultIssueType,
        summary: defaultFields.summary || '',
        description: defaultFields.description || '',
        reporter: reporterValue,
        assignee: assigneeValue,
        priority: defaultPriority,
      });
    }
  }, [
    defaultFields,
    hasDefaultFields,
    issueTypeOptions,
    projectFieldsData,
    reset,
  ]);

  const selectedIssueType = watch('issueType');

  const selectedIssueTypeFields = useMemo(() => {
    if (!projectFieldsData) return [];
    return projectFieldsData[selectedIssueType] || [];
  }, [projectFieldsData, selectedIssueType]);

  const reporterField = useMemo(
    () => selectedIssueTypeFields.find((field) => field.key === 'reporter'),
    [selectedIssueTypeFields],
  );

  const assigneeField = useMemo(
    () => selectedIssueTypeFields.find((field) => field.key === 'assignee'),
    [selectedIssueTypeFields],
  );

  const priorityField = useMemo(
    () => selectedIssueTypeFields.find((field) => field.key === 'priority'),
    [selectedIssueTypeFields],
  );

  const priorityOptions = useMemo(() => {
    if (!priorityField?.allowed_values) return [];
    return priorityField.allowed_values.map((option: any) => {
      const label = option.name || option.label;
      const localIcon = PRIORITY_ICONS[label];
      const fallbackIcon =
        option.iconUrl || option.icon ? (
          <img
            src={option.iconUrl || option.icon}
            alt={label}
            style={{ width: 16, height: 16 }}
          />
        ) : undefined;

      return {
        value: option.id || option.value,
        label,
        icon: localIcon || fallbackIcon,
      };
    });
  }, [priorityField]);

  const hasLimitedPermissions = useMemo(() => {
    const reporterLimited =
      reporterField &&
      (!reporterField.allowed_values ||
        reporterField.allowed_values.length === 0);
    const assigneeLimited =
      assigneeField &&
      (!assigneeField.allowed_values ||
        assigneeField.allowed_values.length === 0);
    return reporterLimited || assigneeLimited;
  }, [reporterField, assigneeField]);

  const typeSpecificFields = useMemo(
    () =>
      selectedIssueTypeFields.filter(
        (field) => !COMMON_FIELD_KEYS.includes(field.key),
      ),
    [selectedIssueTypeFields],
  );

  const isFormReady = hasDefaultFields
    ? !!defaultFields &&
      issueTypeOptions.length > 0 &&
      !!projectFieldsData &&
      !!selectedIssueType
    : issueTypeOptions.length > 0 && !!projectFieldsData;

  const isLoading = hasDefaultFields
    ? isLoadingDefaults || isFetchingFields || !isFormReady
    : isFetchingFields || !isFormReady;

  const projectFieldOptions = useMemo(() => {
    const selectedIssueTypeFieldsInner =
      projectFieldsData?.[selectedIssueType] || [];
    return (
      selectedIssueTypeFieldsInner
        .find((field) => field.key === 'project')
        ?.allowed_values?.map((option) => ({
          value: option.value,
          label: option.label,
        })) || []
    );
  }, [projectFieldsData, selectedIssueType]);

  const formValues = watch();

  const isFormValid = useMemo(() => {
    if (!selectedIssueType) {
      return false;
    }

    if (!formValues.summary?.trim()) {
      return false;
    }

    for (const field of selectedIssueTypeFields) {
      if (field.required) {
        if (COMMON_FIELD_KEYS.includes(field.key)) {
          continue;
        }

        const value = formValues[field.key];

        if (!value || (typeof value === 'string' && !value.trim())) {
          return false;
        }
      }
    }

    return true;
  }, [formValues, selectedIssueTypeFields, selectedIssueType]);

  const onCreateTicket = async () => {
    const vals = formMethods.getValues();
    const transformedValues = await transformJiraFormValues(
      vals,
      selectedIssueTypeFields,
    );
    onConfirm(transformedValues);
  };

  const dynamicDefaultValues =
    hasDefaultFields && defaultFields ? defaultFields : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      titleIcon={<JiraIcon />}
      title="Create Jira ticket"
      confirmText="Create ticket"
      onConfirmButtonClick={onCreateTicket}
      isLoading={isCreating || isLoading}
      confirmDisabled={!isFormValid}
      customWidth={688}
      message={
        <FormProvider {...formMethods}>
          <form
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            style={{
              height: '776px',
            }}
          >
            {isLoading && <Loading />}

            {!isLoading && (
              <FlexColumn gap={2}>
                {error &&
                  (contextId ? (
                    <TicketCreationError
                      error={error}
                      provider="jira"
                      contextId={contextId}
                      integrationId={integration.id}
                    />
                  ) : (
                    <Alert severity="error">
                      {error.message || 'Failed to create Jira ticket.'}
                    </Alert>
                  ))}

                <FormField title="Project">
                  <DropDown
                    size="large"
                    label="Project"
                    options={projectFieldOptions}
                    value={integration.project_id}
                    disabled
                  />
                </FormField>
                <FormField title="Issue type">
                  <DropDown
                    size="large"
                    label="Issue Type"
                    options={issueTypeOptions}
                    value={selectedIssueType}
                    onChange={(value) => setValue('issueType', value)}
                    required
                  />
                </FormField>

                <FormField
                  title="Summary"
                  errorMessage={
                    formMethods.formState.errors.summary?.message as string
                  }
                >
                  <TextField
                    {...formMethods.register('summary', {
                      required: requiredLabel,
                    })}
                    size="large"
                  />
                </FormField>

                <FormField
                  title="Description"
                  errorMessage={
                    formMethods.formState.errors.description?.message as string
                  }
                >
                  <TextField
                    {...formMethods.register('description')}
                    multiline
                    rows={14}
                  />
                </FormField>

                {hasLimitedPermissions && (
                  <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                    Some fields like Reporter and Assignee are limited.
                    Re-authenticate your Jira integration{' '}
                    <InternalLink to="/settings/environments">
                      in the Environments Settings page
                    </InternalLink>{' '}
                    to enable user selection and gain full permissions.
                  </Alert>
                )}

                {reporterField && (
                  <JiraUserAutocompleteField
                    field={reporterField}
                    label="Reporter"
                    envId={envId}
                    integrationId={integration.id}
                    projectId={integration.project_id}
                  />
                )}

                {assigneeField && (
                  <JiraUserAutocompleteField
                    field={assigneeField}
                    label="Assignee"
                    envId={envId}
                    integrationId={integration.id}
                    projectId={integration.project_id}
                  />
                )}

                {priorityField && priorityOptions.length > 0 && (
                  <FormField title="Priority">
                    <DropDown
                      size="large"
                      label="Priority"
                      placeholder="Select priority"
                      options={priorityOptions}
                      value={watch('priority')}
                      onChange={(value) => setValue('priority', value)}
                    />
                  </FormField>
                )}

                <JiraDynamicFields
                  fields={typeSpecificFields}
                  defaultValues={dynamicDefaultValues}
                  integrationId={integration.id}
                  projectKey={projectKey}
                  baseUrl={baseUrl}
                  projectId={integration.project_id}
                  issueType={selectedIssueType}
                />
              </FlexColumn>
            )}
          </form>
        </FormProvider>
      }
    />
  );
};

export default CreateJiraTicketDialog;
